import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import * as React from 'react'

export const Route = createFileRoute('/import')({
  component: ImportPage,
})

type CreateSourceImportResponse = {
  id: string
  status: string
}

function ImportPage() {
  const navigate = useNavigate()
  const [url, setUrl] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const handleSubmit = React.useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const trimmed = url.trim()
      if (!trimmed || submitting) return

      setSubmitting(true)
      setError(null)
      try {
        const res = await fetch('/api/v1/source-imports', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: trimmed }),
        })

        // 201 (created) or any 2xx: redirect to the detail page, which drives progress.
        if (res.ok) {
          const data = (await res.json()) as CreateSourceImportResponse
          navigate({ to: '/import/$id', params: { id: data.id } })
          return
        }

        if (res.status === 422) {
          setError('Unsupported URL. Please use a TikTok, Instagram Reels, or YouTube Shorts link.')
        } else if (res.status === 429) {
          setError('You have reached the maximum number of imports. Try again later.')
        } else if (res.status === 503) {
          setError('Source import is temporarily disabled.')
        } else {
          setError('Something went wrong. Please try again.')
        }
      } catch {
        setError('Unable to connect. Please check your connection and try again.')
      } finally {
        setSubmitting(false)
      }
    },
    [url, submitting, navigate],
  )

  return (
    <div className="ootopia-pwa-page ootopia-pwa-import">
      <div className="ootopia-pwa-import__intro">
        <div>
          <p className="ootopia-section-kicker">Source import</p>
          <h1 className="mt-2 ootopia-editorial-heading">Bring a look into your shelf.</h1>
          <p className="mt-3 text-sm leading-6 text-muted">
            Paste a makeup tutorial URL from TikTok, Instagram Reels, or YouTube Shorts. Ootopia
            turns the visible steps into a usable recipe.
          </p>
        </div>
        <img
          src="/homepage/journey-creator-tutorial.png"
          alt="Creator tutorial with visible makeup steps"
          className="ootopia-pwa-import__media"
          loading="eager"
          decoding="async"
        />
      </div>

      <form onSubmit={handleSubmit} className="ootopia-pwa-import__form">
        <label className="block">
          <span className="sr-only">Paste a makeup tutorial URL</span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.tiktok.com/@creator/video/..."
            disabled={submitting}
            className="ootopia-pwa-field"
          />
        </label>

        <button
          type="submit"
          disabled={submitting || !url.trim()}
          className="ootopia-pwa-import__submit"
        >
          {submitting ? (
            <span
              className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"
              aria-hidden="true"
            />
          ) : null}
          {submitting ? 'Reading the look...' : 'Analyze this look'}
        </button>
      </form>

      {error ? (
        <div className="ootopia-pwa-import__error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <ol className="ootopia-pwa-import__steps" aria-label="Import steps">
        <li>01 Resolve the creator link</li>
        <li>02 Read the visible steps</li>
        <li>03 Map the look to your shelf</li>
      </ol>

      <Link to="/looks" className="ootopia-pwa-import__back">
        Back to looks
      </Link>
    </div>
  )
}
